// @flow
import * as React from 'react';
import styled from 'styled-components';
import { space } from 'styled-system';
import type { ActionType, ActionSubType } from 'api/volops';
import Box from 'components/Box';
import Hide from 'components/Hide';
import { RouterLink } from 'components/Link';
import Text, { H1, H2, H3, H4, H5, Body, BodySmall } from 'components/Text';
import Container from 'components/Container';
import Divider from 'components/Divider';
import Address from 'components/Address';
import SearchContextHeader from 'components/SearchContextHeader';
import type { SearchContext } from 'containers/SearchContextHeaderContainer';
import ListingTypeLabel from 'components/ListingTypeLabel';
import { TrackedAddressLink } from 'components/AddressLink';
import PrintHidden from 'components/PrintHidden';
import { uuid } from 'utils/functional';
import { colors } from '../theme';
import ListingLayout from '../layouts/ListingLayout';

export const SectionContainer = styled.div`
  border-top: solid 1px ${colors.aluminum};
  padding-top: 16px;
  padding-bottom: 16px;
  margin-top: 16px;
  margin-bottom: 32px;
  ${space};
`;

export const SectionTitle = styled(H3)`
  margin-bottom: 24px;
`;

export const Section = ({
  title,
  children,
  ...props
}: {
  title: string,
  children: React.Node
}) => (
  <SectionContainer {...props}>
    <SectionTitle>{title}</SectionTitle>
    {children}
  </SectionContainer>
);

const SidebarLabel = styled(H5)`
  margin: 0;
`;

const SidebarValue = styled(BodySmall)`
  margin-top: 0;
  color: ${colors.battleshipGrey};
  + * {
    margin-top: 16px;
  }
`;

export const SidebarItem = ({
  label,
  value
}: {
  label: React.Node,
  value: ?React.Node
}) => {
  const id = `sidebar-item-${uuid()}`;
  return (
    <>
      <SidebarLabel id={id}>{label}</SidebarLabel>
      <SidebarValue aria-labelledby={id}>{value}</SidebarValue>
    </>
  );
};

export const LocationSection = ({
  address,
  directions,
  listingType,
  linkToMap
}: {
  address: Object,
  directions?: string,
  listingType?: string,
  linkToMap?: boolean
}) => (
  <Section
    title={/* TRANSLATORS: Listing Page, section title */ getText('Location')}
  >
    {address &&
      (listingType && linkToMap ? (
        <TrackedAddressLink
          address={address}
          eventName="Clicked: Listing Address"
          eventProperties={{ type: listingType }}
        />
      ) : (
        <Address address={address} />
      ))}
    {directions && (
      <Box mt={32}>
        <H4 mb={0}>{getText('Directions:')}</H4>
        <Body dangerouslySetInnerHTML={{ __html: directions }} />
      </Box>
    )}
  </Section>
);

const ListingPage = ({
  listingType,
  actionType,
  actionSubType,
  title,
  subtitle,
  details,
  sidebarTitle,
  sidebarImage,
  sidebarHeader,
  sidebarDetails,
  sidebarLinks,
  renderActionButtons,
  searchContext,
  adminActions,
  toolbar,
  isOrg,
  isAdmin,
  isGroupAdmin,
  banner,
  footer,
  url
}: {
  listingType:
    | 'JOB'
    | 'INTERNSHIP'
    | 'VOLOP'
    | 'ORG'
    | 'GROUP'
    | 'IDEALIST_DAY_EVENT',
  actionType?: ?ActionType,
  actionSubType?: ?ActionSubType,
  title: React.Node,
  subtitle: React.Node,
  details: React.Node,
  sidebarTitle?: ?string | ?React.Node,
  sidebarImage?: ?string | ?React.Node,
  sidebarHeader?: React.Node,
  sidebarDetails?: React.Node,
  sidebarLinks: ?React.Node,
  renderActionButtons: (mobile: boolean) => React.Node,
  searchContext: ?SearchContext,
  adminActions?: React.Node,
  toolbar?: React.Node,
  isOrg?: boolean,
  isAdmin?: boolean,
  isGroupAdmin?: boolean,
  banner?: React.Node,
  footer?: React.Node,
  url: Object
}) => (
  <ListingLayout
    adminActions={adminActions}
    toolbar={toolbar}
    isOrg={isOrg}
    isAdmin={isAdmin}
    isGroupAdmin={isGroupAdmin}
    listingType={listingType}
    url={url}
    header={
      <>
        {searchContext && (
          <PrintHidden>
            <SearchContextHeader
              mb={[4, 4, 4, '40px']}
              divider
              {...searchContext}
            />
          </PrintHidden>
        )}
        <Container mt={[0, 2, 2, 2]} mb={[2, 2, 2, 4]}>
          <ListingTypeLabel
            listingType={listingType}
            actionType={actionType}
            actionSubType={actionSubType}
          />
        </Container>
        <H1
          styleAs="h2"
          color="greyishBrown"
          mb="24px"
          data-qa-id="listing-name"
        >
          {title}
        </H1>
        <Text
          fontSize={['20px', '20px', '20px', '22px']}
          color="greyishBrown"
          mb={[3, 3, 3, 4]}
        >
          {subtitle}
        </Text>
        <Hide xs sm md>
          <Container pb={4}>{renderActionButtons(false)}</Container>
        </Hide>
      </>
    }
    buttonBar={renderActionButtons(true)}
    main={
      <>
        {details}
        {searchContext && (
          <PrintHidden>
            <Box mb={48}>
              <SearchContextHeader {...searchContext} divider={false} />
            </Box>
          </PrintHidden>
        )}
      </>
    }
    banner={banner}
    footer={footer}
    sidebarTitle={
      sidebarTitle && (
        <PrintHidden>
          <Hide xs sm md>
            <H2 styleAs="h3" mb={24}>
              {sidebarTitle}
            </H2>
          </Hide>
          <Divider mt={0} mb={[4, 4, 4, '12px']} />
        </PrintHidden>
      )
    }
    sidebarImage={
      sidebarImage && (
        <PrintHidden>
          <Hide xs sm md>
            <Box mb={48}>{sidebarImage}</Box>
          </Hide>
        </PrintHidden>
      )
    }
    sidebar={
      <>
        {sidebarHeader && (
          <Container mb={[2, 2, 2, 4]}>{sidebarHeader}</Container>
        )}
        {sidebarDetails && (
          <>
            {sidebarHeader && (
              <Divider display={['none', 'none', 'none', 'block']} />
            )}
            {sidebarDetails}
          </>
        )}
        {(searchContext || sidebarLinks) && (
          <PrintHidden>
            {(sidebarDetails || sidebarHeader) && <Divider mt="10px" />}
            <Hide xs sm md>
              {searchContext && (
                <Box pb={3}>
                  <RouterLink
                    mr={2}
                    to={searchContext.searchResultsPath}
                    // onClick={trackClickBackToResults}
                    data-qa-id="search-context-back-to-search-sidebar"
                  >
                    {getText('Back to search results')}
                  </RouterLink>
                </Box>
              )}
              {sidebarLinks}
            </Hide>
          </PrintHidden>
        )}
      </>
    }
  />
);

export default ListingPage;
